import React, { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ── Constellation background ─────────────────────────────────────────────────
function ConstellationBg() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf, stars = [];
    const N = 140;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    const handleMouseMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("mousemove", handleMouseMove);

    for (let i = 0; i < N; i++) {
      const vx = (Math.random() - 0.5) * 0.1;
      const vy = (Math.random() - 0.5) * 0.1;
      stars.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        ox: vx, oy: vy, vx, vy,
        r: Math.random() * 1.5 + 0.2,
        alpha: Math.random() * 0.6 + 0.2,
        pulse: Math.random() * Math.PI * 2,
        twinkle: Math.random() * 0.02 + 0.005,
      });
    }

    function draw() {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      const bg = ctx.createRadialGradient(W * 0.25, H * 0.2, 0, W * 0.5, H * 0.5, W);
      bg.addColorStop(0, "#080d1f"); bg.addColorStop(0.4, "#050810"); bg.addColorStop(1, "#020408");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      [
        { x: W * 0.1, y: H * 0.15, r: W * 0.3, c: [20, 50, 180, 0.04] },
        { x: W * 0.85, y: H * 0.55, r: W * 0.28, c: [100, 20, 200, 0.05] },
        { x: W * 0.45, y: H * 0.85, r: W * 0.22, c: [0, 100, 220, 0.035] },
        { x: W * 0.6, y: H * 0.1, r: W * 0.18, c: [60, 20, 160, 0.03] },
      ].forEach(nb => {
        const [r, g, b, a] = nb.c;
        const grd = ctx.createRadialGradient(nb.x, nb.y, 0, nb.x, nb.y, nb.r);
        grd.addColorStop(0, `rgba(${r},${g},${b},${a})`); grd.addColorStop(1, "transparent");
        ctx.fillStyle = grd; ctx.fillRect(0, 0, W, H);
      });

      const mx = mouseRef.current.x, my = mouseRef.current.y;
      const ATTRACT_RADIUS = 200, ATTRACT_STRENGTH = 0.013;

      stars.forEach(s => {
        const dx = mx - s.x, dy = my - s.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < ATTRACT_RADIUS && dist > 0) {
          const force = (1 - dist / ATTRACT_RADIUS) * ATTRACT_STRENGTH;
          s.vx += dx * force * 0.3; s.vy += dy * force * 0.3;
        }
        s.vx += (s.ox - s.vx) * 0.015; s.vy += (s.oy - s.vy) * 0.015;
        s.x += s.vx; s.y += s.vy; s.pulse += s.twinkle;
        if (s.x < -2) s.x = W + 2; if (s.x > W + 2) s.x = -2;
        if (s.y < -2) s.y = H + 2; if (s.y > H + 2) s.y = -2;
        const a = s.alpha * (0.6 + 0.4 * Math.sin(s.pulse));
        const proximity = dist < ATTRACT_RADIUS ? (1 - dist / ATTRACT_RADIUS) : 0;
        if (s.r > 1.1 || proximity > 0.3) {
          const glowR = s.r * (4 + proximity * 6);
          const glow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, glowR);
          glow.addColorStop(0, `rgba(160,200,255,${a * (0.5 + proximity * 0.5)})`);
          glow.addColorStop(1, "transparent");
          ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(s.x, s.y, glowR, 0, Math.PI * 2); ctx.fill();
        }
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r * (1 + proximity * 0.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,220,255,${a})`; ctx.fill();
      });

      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].x - stars[j].x, dy = stars[i].y - stars[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 120) {
            const fade = (1 - d / 120) * 0.45;
            const dm1 = Math.sqrt((stars[i].x - mx) ** 2 + (stars[i].y - my) ** 2);
            const dm2 = Math.sqrt((stars[j].x - mx) ** 2 + (stars[j].y - my) ** 2);
            const near = Math.min(dm1, dm2) < 200;
            ctx.strokeStyle = near ? `rgba(120,180,255,${fade * 1.8})` : `rgba(80,130,255,${fade})`;
            ctx.lineWidth = near ? 0.9 : 0.6;
            ctx.beginPath(); ctx.moveTo(stars[i].x, stars[i].y); ctx.lineTo(stars[j].x, stars[j].y); ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    }
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }} />;
}

const NAV = [
  { id: "dashboard", label: "Dashboard", path: "/",        icon: <GridIcon /> },
  { id: "servers",   label: "Servers",   path: "/servers", icon: <MonitorIcon /> },
  { id: "tencent",   label: "Tencent",   path: "/tencent", icon: <CloudIcon /> },
  { id: "tencent80", label: "Tencent80", path: "/tencent80", icon: <CloudIcon /> },
  { id: "iotamine",  label: "Iotamine",  path: "/iotamine", icon: <CloudIcon /> },
  { id: "cookies",   label: "Cookies",   path: "/zoom-cookies", icon: <CookieIcon />, adminOnly: true },
  { id: "admin",     label: "Admin",     path: "/adminhanuman",   icon: <ShieldIcon />, adminOnly: true },
  { id: "payments",  label: "Payments",  path: "/payments",icon: <PayIcon />,   adminOnly: true },
  { id: "broadcast", label: "Broadcast", path: "/broadcast",icon: <BroadcastIcon />, adminOnly: true },
];

export default function Layout({ children, onLogout, userInfo = {} }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const page      = location.pathname;

  const [avatar, setAvatar] = useState(() => localStorage.getItem("bc_avatar") || null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [serverOnline, setServerOnline] = useState(true);
  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || "");
    const token = () => localStorage.getItem("token") || "";
    let failCount = 0;
    async function checkServer() {
      try {
        const r = await fetch(API + "/api/agents", {
          headers: { Authorization: "Bearer " + token() }
        });
        if (r.ok) { failCount = 0; setServerOnline(true); }
        else { failCount++; if (failCount >= 3) setServerOnline(false); }
      } catch {
        failCount++;
        if (failCount >= 3) setServerOnline(false);
      }
    }
    checkServer();
    const i = setInterval(checkServer, 15000);
    return () => clearInterval(i);
  }, []);
 
  const fileRef = useRef();

  // Custom draggable scrollbar for .adm-content (2026-10-22) — the browser's
  // own scrollbar wasn't rendering visibly for the user even with
  // ::-webkit-scrollbar styling + overflow-y:scroll (some browsers/privacy
  // settings suppress custom native-scrollbar styling entirely), so this
  // draws and drives its own thumb instead of depending on that at all.
  const contentRef = useRef(null);
  const [thumb, setThumb] = useState({ top: 0, height: 0, visible: false });
  const dragState = useRef(null); // { startY, startScrollTop } while dragging

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const recalc = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight) { setThumb(t => t.visible ? { ...t, visible: false } : t); return; }
      const trackHeight = clientHeight;
      const height = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
      const top = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - height);
      setThumb({ top, height, visible: true });
    };

    // rAF-debounced — content (API-loaded tables, cards, etc.) can mutate
    // rapidly, and ResizeObserver alone only catches .adm-content's own box
    // resizing, not its scrollHeight growing from children changing while
    // the box itself stays the same size — the MutationObserver below is
    // what actually catches that.
    let raf = null;
    const scheduleRecalc = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; recalc(); });
    };

    recalc();
    el.addEventListener("scroll", recalc);
    const ro = new ResizeObserver(scheduleRecalc);
    ro.observe(el);
    const mo = new MutationObserver(scheduleRecalc);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", scheduleRecalc);
    return () => {
      el.removeEventListener("scroll", recalc);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", scheduleRecalc);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    dragState.current = { startY: e.clientY, startScrollTop: contentRef.current.scrollTop };
    const onMove = (ev) => {
      const el = contentRef.current;
      if (!el || !dragState.current) return;
      const { scrollHeight, clientHeight } = el;
      const trackHeight = clientHeight;
      const deltaY = ev.clientY - dragState.current.startY;
      const scrollableDist = scrollHeight - clientHeight;
      const thumbTravel = trackHeight - thumb.height;
      if (thumbTravel <= 0) return;
      el.scrollTop = dragState.current.startScrollTop + (deltaY / thumbTravel) * scrollableDist;
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      localStorage.setItem("bc_avatar", ev.target.result);
      setAvatar(ev.target.result); setProfileOpen(false);
    };
    reader.readAsDataURL(file);
  };

  const [broadcast, setBroadcast] = useState(null);
  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || "");
    const token = () => localStorage.getItem("token") || "";
    async function fetchBroadcast() {
      try {
        const r = await fetch(API + "/api/broadcast/active", { headers: { Authorization: "Bearer " + token() } });
        if (r.ok) { const d = await r.json(); setBroadcast(d.message || null); }
        else setBroadcast(null);
      } catch { setBroadcast(null); }
    }
    fetchBroadcast();
    const i = setInterval(fetchBroadcast, 15000);
    return () => clearInterval(i);
  }, []);

  const isActive = (path) => {
    if (path === "/") return page === "/";
    // Exact match, or a genuine sub-route (page.startsWith(path) alone
    // wrongly matched "/tencent" against "/tencent80" too, since it's just
    // a string prefix — both nav items lit up at once. Requiring the next
    // char to be "/" (or nothing) makes "/tencent/x" still match "/tencent"
    // without "/tencent80" falsely matching it.
    return page === path || page.startsWith(path + "/");
  };

  const uname = userInfo.username || "Admin";

const handleNavClick = (path) => { navigate(path); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'Outfit',sans-serif;background:#020408;-webkit-font-smoothing:antialiased;color:#e2e8f0;}
        ::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.07);border-radius:4px;}
        @keyframes floatUp{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);}}
        @keyframes pulse{0%,100%{box-shadow:0 0 0 3px rgba(74,222,128,.2)}50%{box-shadow:0 0 0 5px rgba(74,222,128,.05)}}
        @keyframes ddOpen{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
        @keyframes slideIn{from{transform:translateX(-100%);}to{transform:translateX(0);}}

        /* Layout shell */
        .adm-shell{display:flex;min-height:100vh;position:relative;z-index:1;}

        /* Sidebar */
        .adm-sidebar{
          width:220px;flex-shrink:0;
          background:rgba(6,10,22,0.88);
          backdrop-filter:blur(28px) saturate(1.5);
          border-right:1px solid rgba(255,255,255,0.08);
          display:flex;flex-direction:column;
          position:sticky;top:0;height:100vh;height:100dvh;overflow-y:auto;
          z-index:100;
          transition: transform 0.25s ease;
        }
        .adm-logo{padding:20px 18px 16px;display:flex;align-items:center;gap:12px;border-bottom:1px solid rgba(255,255,255,0.07);}
        .adm-logo-mark{
          width:38px;height:38px;border-radius:11px;
          background:linear-gradient(135deg,#38bdf8,#818cf8);
          display:flex;align-items:center;justify-content:center;font-size:19px;flex-shrink:0;
          box-shadow:0 0 20px rgba(56,189,248,.4);
        }
        .adm-logo-title{font-size:14px;font-weight:800;color:#f1f5f9;letter-spacing:-.3px;}
        .adm-logo-sub{font-size:9px;color:#4a6280;letter-spacing:2.5px;text-transform:uppercase;margin-top:2px;}

        .adm-nav{padding:12px 0;flex:1;}
        .adm-nav-section{padding:14px 16px 6px;font-size:9px;font-weight:700;color:#334155;text-transform:uppercase;letter-spacing:1.2px;}
        .adm-nav-item{
          display:flex;align-items:center;gap:9px;padding:9px 16px;cursor:pointer;
          color:#f8fafc;font-size:14px;font-weight:700;
          border-left:2px solid transparent;transition:all .15s;margin:1px 8px;border-radius:8px;
        }
        .adm-nav-item:hover{color:#38bdf8;background:rgba(255,255,255,0.03);}
        .adm-nav-item.active{
          color:#38bdf8;font-weight:800;
          background:rgba(56,189,248,0.14);
          border-left-color:#38bdf8;
          box-shadow:inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .adm-nav-badge{
          margin-left:auto;background:#38bdf8;color:#fff;
          font-size:10px;font-weight:700;padding:1px 7px;border-radius:20px;
        }

        .adm-sidebar-bottom{
          padding:14px;border-top:1px solid rgba(255,255,255,0.06);
        }
        .adm-status-row{
          display:flex;align-items:center;gap:8px;
          background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.06);
          border-radius:10px;padding:8px 12px;font-size:12px;
        }
        .adm-pulse-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
        .adm-pulse-green{background:#4ade80;animation:pulse 2s infinite;}
        .adm-pulse-gray{background:#334155;}

        /* Mobile overlay */
        .adm-sidebar-overlay{
          display:none;
          position:fixed;inset:0;
          background:rgba(0,0,0,0.7);
          backdrop-filter:blur(4px);
          z-index:99;
        }
        .adm-sidebar-overlay.open{display:block;}

        /* Hamburger button */
        .adm-hamburger{
          display:none;
          background:rgba(255,255,255,0.05);
          border:1px solid rgba(255,255,255,0.1);
          border-radius:8px;
          padding:7px 9px;
          cursor:pointer;
          flex-direction:column;
          gap:4px;
          align-items:center;
          justify-content:center;
        }
        .adm-hamburger span{
          display:block;width:18px;height:2px;
          background:#94a3b8;border-radius:2px;
          transition:all 0.2s;
        }
        .adm-hamburger.open span:nth-child(1){transform:translateY(6px) rotate(45deg);}
        .adm-hamburger.open span:nth-child(2){opacity:0;}
        .adm-hamburger.open span:nth-child(3){transform:translateY(-6px) rotate(-45deg);}

        /* Main area */
        .adm-main{
          flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;
          height:100vh;height:100dvh;
        }

        /* Topbar */
        .adm-topbar{
          background:rgba(6,10,22,0.88);backdrop-filter:blur(28px);
          border-bottom:1px solid rgba(255,255,255,0.07);
          padding:0 28px;height:64px;display:flex;align-items:center;justify-content:space-between;
          position:sticky;top:0;z-index:50;
          gap:10px;
        }
        .adm-topbar-left{display:flex;align-items:center;gap:12px;}
        .adm-topbar-title{font-size:16px;font-weight:800;color:#f1f5f9;letter-spacing:-.3px;}

        /* Content — custom JS-driven scrollbar (2026-10-22). The browser's
           own scrollbar wasn't rendering visibly for the user even with
           ::-webkit-scrollbar styling (some browsers/privacy settings
           suppress custom native-scrollbar styling), so the real scrollbar
           is hidden entirely and replaced with our own draggable thumb
           (.adm-content-scrollbar-thumb, driven from JS in Layout.jsx). */
        .adm-content-wrap{flex:1;min-width:0;min-height:0;position:relative;display:flex;overflow:hidden;}
        .adm-content{
          padding:24px 28px;flex:1;min-height:0;overflow-y:auto;animation:fadeIn .2s ease;
          scrollbar-width:none;
        }
        .adm-content::-webkit-scrollbar{display:none;}
        .adm-content-scrollbar-thumb{
          position:absolute;right:4px;width:10px;border-radius:6px;
          background:#ffffff;cursor:pointer;
          box-shadow:0 0 6px rgba(255,255,255,0.6);
          transition:background .15s;
        }
        .adm-content-scrollbar-thumb:hover{background:#e2e8f0;}

        /* Cards */
        .adm-card{
          background:rgba(8,13,32,0.72);
          backdrop-filter:blur(28px) saturate(1.5);
          border:1px solid rgba(255,255,255,0.09);
          border-radius:16px;
          box-shadow:0 8px 48px rgba(0,0,0,0.6),inset 0 1px 0 rgba(255,255,255,0.06);
          transition:border-color .25s,box-shadow .25s,transform .2s;
        }
        .adm-card:hover{
          border-color:rgba(99,179,237,0.2);
          box-shadow:0 14px 58px rgba(0,0,0,.65),0 0 0 1px rgba(99,179,237,0.08),inset 0 1px 0 rgba(255,255,255,.06);
        }
        .adm-card-pad{padding:20px;}
        .adm-card-header{
          padding:14px 20px;border-bottom:1px solid rgba(255,255,255,0.07);
          display:flex;align-items:center;justify-content:space-between;
          border-radius:16px 16px 0 0;
        }
        .adm-card-title{font-size:14px;font-weight:800;color:#cbd5e1;letter-spacing:-.1px;display:flex;align-items:center;gap:9px;}
        .adm-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;display:inline-block;}

        /* Stat cards */
        .adm-stat-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:20px;}
        .adm-stat-card{
          background:rgba(8,13,32,0.72);backdrop-filter:blur(28px) saturate(1.5);
          border:1px solid rgba(255,255,255,0.09);border-radius:16px;
          box-shadow:0 8px 48px rgba(0,0,0,0.6),inset 0 1px 0 rgba(255,255,255,0.06);
          padding:22px 22px 18px;position:relative;overflow:hidden;
          transition:border-color .25s,box-shadow .25s,transform .22s;
          animation:floatUp .4s ease both;
        }
        .adm-stat-card:hover{transform:translateY(-4px);}
        .adm-stat-card[data-c="blue"]:hover  {border-color:rgba(56,189,248,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(56,189,248,0.22)!important;}
        .adm-stat-card[data-c="green"]:hover {border-color:rgba(74,222,128,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(74,222,128,0.18)!important;}
        .adm-stat-card[data-c="amber"]:hover {border-color:rgba(251,191,36,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(251,191,36,0.18)!important;}
        .adm-stat-card[data-c="purple"]:hover{border-color:rgba(192,132,252,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(192,132,252,0.18)!important;}
        .adm-stat-bar{position:absolute;top:0;left:0;right:0;height:3px;}
        .adm-stat-watermark{position:absolute;bottom:8px;right:14px;font-size:48px;opacity:.08;line-height:1;font-weight:900;}
        .adm-stat-val{font-size:48px;font-weight:900;letter-spacing:-3px;line-height:1;margin-bottom:8px;}
        .adm-stat-lbl{font-size:11px;letter-spacing:2px;text-transform:uppercase;font-weight:800;color:#f1f5f9;}

        /* Tables */
        .adm-table-wrap{overflow-x:auto;}
        .adm-table{width:100%;border-collapse:collapse;}
        .adm-table th{text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.9px;color:#f1f5f9;font-weight:800;padding:10px 16px;border-bottom:1px solid rgba(255,255,255,0.06);}
        .adm-table td{padding:12px 16px;border-bottom:1px solid rgba(255,255,255,0.04);font-size:13px;vertical-align:middle;color:#e2e8f0;}
        .adm-table tr:last-child td{border-bottom:none;}
        .adm-table tr:hover td{background:rgba(99,179,237,0.03);}

        /* Badges */
        .adm-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;white-space:nowrap;letter-spacing:.4px;}
        .adm-badge-green{background:rgba(74,222,128,0.1);color:#4ade80;border:1px solid rgba(74,222,128,0.25);}
        .adm-badge-red{background:rgba(248,113,113,0.1);color:#f87171;border:1px solid rgba(248,113,113,0.25);}
        .adm-badge-amber{background:rgba(251,191,36,0.1);color:#fbbf24;border:1px solid rgba(251,191,36,0.25);}
        .adm-badge-blue{background:rgba(99,179,237,0.1);color:#63b3ed;border:1px solid rgba(99,179,237,0.25);}
        .adm-badge-gray{background:rgba(255,255,255,0.05);color:#cbd5e1;border:1px solid rgba(255,255,255,0.08);}

        /* Progress bar */
        .adm-prog{height:4px;background:rgba(255,255,255,0.05);border-radius:3px;overflow:hidden;}
        .adm-prog-fill{height:100%;border-radius:3px;transition:width .5s ease;}

        /* Buttons */
        .adm-btn{
          display:inline-flex;align-items:center;gap:6px;
          padding:7px 14px;border-radius:8px;border:1px solid rgba(255,255,255,0.1);
          background:rgba(255,255,255,0.05);color:#94a3b8;
          font-size:13px;font-weight:600;cursor:pointer;
          font-family:'Outfit',sans-serif;transition:all .15s;
        }
        .adm-btn:hover{background:rgba(255,255,255,0.08);color:#e2e8f0;border-color:rgba(255,255,255,0.15);}
        .adm-btn:disabled{opacity:.5;cursor:not-allowed;}
        .adm-btn-primary{
          background:linear-gradient(135deg,#38bdf8,#818cf8);color:#fff;
          border:none;box-shadow:0 4px 16px rgba(56,189,248,.25);
        }
        .adm-btn-primary:hover{transform:translateY(-1px);box-shadow:0 8px 24px rgba(56,189,248,.35);}
        .adm-btn-danger{background:rgba(248,113,113,0.08);color:#f87171;border-color:rgba(248,113,113,0.22);}
        .adm-btn-danger:hover{background:rgba(248,113,113,0.15);}
        .adm-btn-success{background:rgba(74,222,128,0.08);color:#4ade80;border-color:rgba(74,222,128,0.22);}
        .adm-btn-sm{padding:5px 11px;font-size:12px;}
        .adm-btn-ghost{background:transparent;border-color:transparent;color:#4a6280;}
        .adm-btn-ghost:hover{color:#94a3b8;background:rgba(255,255,255,0.04);border-color:transparent;}

        /* Forms */
        .adm-input{
          width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.13);
          color:#e2e8f0;border-radius:9px;padding:9px 13px;font-size:13px;
          font-family:'Outfit',sans-serif;outline:none;box-sizing:border-box;transition:border-color .18s,box-shadow .18s;
        }
        .adm-input:focus{border-color:rgba(99,179,237,0.55);box-shadow:0 0 0 3px rgba(99,179,237,0.09);}
        .adm-input::placeholder{color:rgba(148,163,184,0.4);}
        select.adm-input{appearance:none;cursor:pointer;}
        .adm-label{display:block;font-size:10px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:#e2e8f0;margin-bottom:5px;}
        .adm-form-row{margin-bottom:15px;}
        .adm-form-err{color:#f87171;font-size:12px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.2);padding:8px 12px;border-radius:8px;margin-top:8px;}

        /* Modal */
        .adm-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.85);backdrop-filter:blur(12px);display:flex;align-items:center;justify-content:center;z-index:500;opacity:0;pointer-events:none;transition:opacity .15s;}
        .adm-modal-bg.open{opacity:1;pointer-events:all;}
        .adm-modal{
          background:rgba(8,13,32,0.96);backdrop-filter:blur(28px);
          border:1px solid rgba(255,255,255,0.1);border-radius:18px;
          box-shadow:0 24px 80px rgba(0,0,0,.8);
          padding:28px;width:500px;max-width:calc(100vw - 40px);
          transform:translateY(14px);transition:transform .18s;animation:floatUp .18s ease;
        }
        .adm-modal-bg.open .adm-modal{transform:translateY(0);}
        .adm-modal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;}
        .adm-modal-title{font-size:16px;font-weight:800;color:#f1f5f9;}
        .adm-modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:22px;padding-top:18px;border-top:1px solid rgba(255,255,255,0.07);}

        /* Agent cards */
        .adm-agent-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(290px,1fr));gap:16px;}
        .adm-agent-card{padding:18px;display:flex;flex-direction:column;gap:13px;border-radius:16px;}

        /* Log box */
        .adm-log-box{
          background:rgba(2,4,8,0.8);border:1px solid rgba(255,255,255,0.06);
          border-radius:10px;padding:12px 14px;height:260px;overflow-y:auto;
          font-family:'JetBrains Mono',monospace;font-size:11.5px;
        }
        .adm-log-line{padding:2px 0;border-bottom:1px solid rgba(255,255,255,0.025);display:flex;gap:8px;align-items:baseline;}
        .adm-log-line:last-child{border-bottom:none;}
        .adm-log-ts{color:#334155;flex-shrink:0;font-size:10.5px;}
        .adm-log-src{color:#38bdf8;flex-shrink:0;}
        .adm-log-msg{color:#94a3b8;}
        .adm-log-line.warn .adm-log-msg{color:#fbbf24;}
        .adm-log-line.error .adm-log-msg{color:#f87171;}

        /* Admin tabs */
        .adm-tabs{display:flex;gap:4px;margin-bottom:18px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.07);border-radius:12px;padding:4px;width:fit-content;}
        .adm-tab{display:flex;align-items:center;gap:6px;padding:7px 16px;border-radius:8px;font-size:13px;font-weight:600;color:#4a6280;cursor:pointer;background:none;border:none;font-family:'Outfit',sans-serif;transition:all .13s;letter-spacing:.1px;}
        .adm-tab:hover{background:rgba(255,255,255,0.04);color:#94a3b8;}
        .adm-tab.active{background:rgba(56,189,248,0.1);color:#e2e8f0;box-shadow:0 0 0 1px rgba(56,189,248,0.2);}

        /* Empty state */
        .adm-empty{text-align:center;padding:36px 20px;color:#334155;font-size:13px;}

        /* TD actions */
        .adm-td-actions{display:flex;gap:6px;align-items:center;flex-wrap:wrap;}

        /* Mono */
        .adm-mono{font-family:'JetBrains Mono',monospace;font-size:11px;color:#f1f5f9;font-weight:600;}
        .adm-mono-box{
          background:rgba(2,4,8,0.8);border:1px solid rgba(255,255,255,0.07);
          border-radius:9px;padding:10px 13px;font-family:'JetBrains Mono',monospace;
          font-size:12px;word-break:break-all;user-select:all;line-height:1.75;
        }

        /* ── Broadcast Ticker ── */
        .bc-ticker-wrap{
          position:fixed;top:0;left:0;right:0;z-index:9999;
          background:linear-gradient(90deg,rgba(56,189,248,0.15),rgba(129,140,248,0.15));
          border-bottom:1px solid rgba(56,189,248,0.3);
          height:36px;overflow:hidden;display:flex;align-items:center;
          backdrop-filter:blur(8px);
        }
        .bc-ticker-label{
          flex-shrink:0;padding:0 16px;
          font-size:11px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;
          color:#38bdf8;border-right:1px solid rgba(56,189,248,0.3);
          white-space:nowrap;display:flex;align-items:center;gap:6px;
        }
        .bc-ticker-track{flex:1;overflow:hidden;position:relative;}
        .bc-ticker-inner{
          display:inline-flex;white-space:nowrap;
          animation:ticker-scroll 20s linear infinite;
        }
        .bc-ticker-inner:hover{animation-play-state:paused;}
        .bc-ticker-text{
          padding:0 60px;font-size:13px;font-weight:600;
          color:#e2e8f0;letter-spacing:.3px;
        }
        @keyframes ticker-scroll{
          0%{transform:translateX(0);}
          100%{transform:translateX(-50%);}
        }
        /* Push content down when ticker is showing */
        .bc-ticker-active .adm-topbar{top:36px;}
        .bc-ticker-active .adm-sidebar{top:36px;height:calc(100vh - 36px);height:calc(100dvh - 36px);}
        /* .adm-shell gets marginTop:36 when the ticker is active (pushing
           the whole shell down), but .adm-main's own height:100vh/100dvh is
           an absolute value unaware of that shift — without this override
           it claims a full viewport height on top of the 36px offset and
           runs 36px past the bottom of the screen. */
        .bc-ticker-active .adm-main{height:calc(100vh - 36px);height:calc(100dvh - 36px);}

        /* ── Responsive ── */
        @media(max-width:960px){
          .adm-stat-grid{grid-template-columns:repeat(2,1fr);}
        }

        @media(max-width:768px){
          /* Show hamburger */
          .adm-hamburger{display:flex;}

          /* Native touch-scroll works fine on mobile — the custom thumb is
             a desktop mouse-drag affordance only. */
          .adm-content-scrollbar-thumb{display:none;}

          /* Sidebar becomes a fixed drawer */
          .adm-sidebar{
            position:fixed;
            top:0;left:0;
            height:100vh;height:100dvh;
            transform:translateX(-100%);
            z-index:200;
            width:240px;
          }
          .adm-sidebar.open{
            transform:translateX(0);
            animation:slideIn 0.25s ease;
          }

          /* Show overlay when open */
          .adm-sidebar-overlay{display:none;}
          .adm-sidebar-overlay.open{display:block;}

          /* Topbar padding */
          .adm-topbar{padding:0 14px;height:56px;}
          .adm-topbar-title{font-size:14px;}

          /* Content padding */
          .adm-content{padding:12px 14px;}

          /* Stat cards: 2 cols on tablet */
          .adm-stat-grid{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:14px;}
          .adm-stat-val{font-size:36px;}

          /* Hide offline chip on small screens */
          .adm-offline-chip{display:none;}

          /* Topbar right: tighten */
          .adm-topbar-right{gap:6px!important;}
          .adm-signout-label{display:none;}
        }

        @media(max-width:480px){
          /* Stat cards: 2 cols on phone */
          .adm-stat-grid{grid-template-columns:repeat(2,1fr);gap:8px;}
          .adm-stat-card{padding:14px 14px 12px;}
          .adm-stat-val{font-size:28px;letter-spacing:-2px;}
          .adm-stat-lbl{font-size:9px;letter-spacing:1px;}
          .adm-stat-watermark{font-size:34px;}

          .adm-content{padding:10px 10px;}
          .adm-topbar{padding:0 10px;}
        }
      `}</style>

      <ConstellationBg />

      {broadcast && (
        <div className="bc-ticker-wrap">
          <div className="bc-ticker-label">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.5c0 2.485-4.477 4.5-10 4.5S2 10.985 2 8.5 6.477 4 12 4s10 2.015 10 4.5z"/><path d="M2 8.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/><path d="M2 12c0 2.485 4.477 4.5 10 4.5S22 14.485 22 12"/><path d="M2 15.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/></svg>
            Notice
          </div>
          <div className="bc-ticker-track">
            <div className="bc-ticker-inner">
              <span className="bc-ticker-text">{broadcast}</span>
              <span className="bc-ticker-text">{broadcast}</span>
            </div>
          </div>
        </div>
      )}
      <div className="adm-shell" style={broadcast ? {marginTop:36} : {}}>
        {/* ── Sidebar ── */}
        <aside className="adm-sidebar">
          <div className="adm-logo">
            <div className="adm-logo-mark">⚡</div>
            <div>
              <div className="adm-logo-title">BotCrowd</div>
              <div className="adm-logo-sub">ZOOM EDITION V5</div>
            </div>
          </div>

          <nav className="adm-nav">
            <div className="adm-nav-section">Menu</div>
            {NAV.filter(n => !n.adminOnly || userInfo.role === "admin").map(n => (
              <div
                key={n.id}
                className={`adm-nav-item${isActive(n.path) ? " active" : ""}`}
                onClick={() => handleNavClick(n.path)}
              >
                {n.icon}
                {n.label}
              </div>
            ))}
          </nav>

          <div className="adm-sidebar-bottom">
            <div className="adm-status-row">
              <span className="adm-pulse-dot adm-pulse-green" />
              <span style={{ fontSize: 12, color: "#4a6280" }}>System Live</span>
            </div>
          </div>
        </aside>

        {/* ── Main ── */}
        <div className="adm-main">
          {/* Topbar */}
          <div className="adm-topbar">
            <div className="adm-topbar-left">
              <span className="adm-topbar-title">
                {NAV.find(n => isActive(n.path))?.label || "Dashboard"}
              </span>
            </div>

            <div className="adm-topbar-right" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Server status chip — hidden on mobile */}
              <div className="adm-offline-chip" style={{ display: "flex", alignItems: "center", gap: 7, background: serverOnline ? "rgba(74,222,128,0.06)" : "rgba(248,113,113,0.06)", border: `1px solid ${serverOnline ? "rgba(74,222,128,0.2)" : "rgba(248,113,113,0.2)"}`, borderRadius: 30, padding: "5px 14px", transition: "all 0.4s" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: serverOnline ? "#4ade80" : "#f87171", flexShrink: 0, display: "inline-block", boxShadow: serverOnline ? "0 0 6px #4ade80" : "none", animation: serverOnline ? "pulse 2s infinite" : "none" }} />
                <span style={{ fontSize: 12, color: serverOnline ? "#4ade80" : "#f87171", fontWeight: 600, transition: "color 0.4s" }}>{serverOnline ? "Online" : "Offline"}</span>
              </div>

              {/* Avatar / user */}
              <div style={{ position: "relative" }}>
                <div
                  onClick={() => setProfileOpen(o => !o)}
                  style={{ display: "flex", alignItems: "center", gap: 9, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 32, padding: "5px 14px 5px 6px", cursor: "pointer" }}
                >
                  {avatar
                    ? <img src={avatar} alt="av" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} />
                    : <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#38bdf8,#818cf8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff" }}>{uname[0]?.toUpperCase()}</div>
                  }
                  <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{uname}</span>
                </div>
                {profileOpen && (
                  <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "rgba(6,10,22,0.97)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: 16, width: 200, zIndex: 300, boxShadow: "0 24px 60px rgba(0,0,0,.8)", backdropFilter: "blur(24px)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>Account</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 2 }}>{uname}</div>
                    <div style={{ fontSize: 11, color: "#4a6280", marginBottom: 12 }}>{userInfo.role === "admin" ? "👑 Admin" : "👤 User"}</div>
                    <button className="adm-btn adm-btn-sm" style={{ width: "100%", justifyContent: "center", marginBottom: 6 }} onClick={() => fileRef.current.click()}>
                      {avatar ? "Change Photo" : "Upload Photo"}
                    </button>
                    {avatar && <button className="adm-btn adm-btn-sm adm-btn-danger" style={{ width: "100%", justifyContent: "center" }} onClick={() => { localStorage.removeItem("bc_avatar"); setAvatar(null); setProfileOpen(false); }}>Remove</button>}
                    <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />
                  </div>
                )}
              </div>

              <button
  onClick={onLogout}
  style={{ background: "transparent", border: "1px solid rgba(248,113,113,0.28)", color: "#f87171", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, transition: "all .15s", display: "flex", alignItems: "center", gap: 6 }}
>
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
  <span className="adm-signout-label">Sign out</span>
</button>
            </div>
          </div>

          <div className="adm-content-wrap">
            <div className="adm-content" ref={contentRef}>
              {children}
            </div>
            {thumb.visible && (
              <div
                className="adm-content-scrollbar-thumb"
                style={{ top: thumb.top, height: thumb.height }}
                onMouseDown={onThumbMouseDown}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Icons
function GridIcon()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function MonitorIcon() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>; }
function CloudIcon()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>; }
function CookieIcon()  { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5z"/><circle cx="9" cy="10" r="1"/><circle cx="14" cy="15" r="1"/><circle cx="8.5" cy="15.5" r="1"/></svg>; }
function PayIcon()     { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>; }
function BroadcastIcon(){ return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.5c0 2.485-4.477 4.5-10 4.5S2 10.985 2 8.5 6.477 4 12 4s10 2.015 10 4.5z"/><path d="M2 8.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/><path d="M2 12c0 2.485 4.477 4.5 10 4.5S22 14.485 22 12"/><path d="M2 15.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/></svg>; }
function ShieldIcon()  { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>; }